import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import { changeFreightNewUserStatus, updateFreightNewUserVerifyStatus } from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import { FREIGHT_NEW_USER_COLUMNS } from "../../../config/freightAdminColumns";
import { useFreightAdminTablePrefs } from "../../../hooks/useFreightAdminTablePrefs";
import FreightAdminTableToolbar from "../../../components/admin/FreightAdminTableToolbar";
import FreightUsersServerList from "../../../components/admin/FreightUsersServerList";
import { DataTablePageSize } from "../../../components/datatable";
import { FREIGHT_DT_LENGTH_MENU_25 } from "../../../components/admin/freightServerSideDtDefaults";
import "./adminFreight.css";

const TABLE_ID = "freight_new_users";
const COLUMN_DEFS = FREIGHT_NEW_USER_COLUMNS;

const FreightNewUsersPage = () => {
  const navigate = useNavigate();
  const getNavigate = useCallback(() => navigate, [navigate]);
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = [
    { label: roleLabel, link: "/dashboard" },
    { label: "Freight", link: "/freight" },
    { label: "New users" }
  ];

  const columnDefs = useMemo(() => COLUMN_DEFS, []);
  const lengthMenu = useMemo(() => Array.from(FREIGHT_DT_LENGTH_MENU_25[0]), []);
  const [pageSize, setPageSize] = useState(() => FREIGHT_DT_LENGTH_MENU_25[0][0]);

  const {
    visibility,
    toggleColumn,
    resetColumnsVisible,
    resetColumnOrder,
    orderedColumnDefs,
    onColumnReorder
  } = useFreightAdminTablePrefs(TABLE_ID, columnDefs);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const tableRef = useRef(null);

  const handleCounts = useCallback((filtered, total) => {
    setRecordsFiltered(typeof filtered === "number" && filtered >= 0 ? filtered : 0);
    setRecordsTotal(typeof total === "number" && total >= 0 ? total : 0);
  }, []);

  const reloadTable = useCallback(() => {
    tableRef.current?.reload?.();
  }, []);

  const deactivate = async (userId) => {
    try {
      await changeFreightNewUserStatus({ user_id: String(userId) });
      showSuccess("Updated");
      reloadTable();
    } catch (e) {
      showError(e.message || "Failed");
    }
  };

  const verify = async (userId, status) => {
    try {
      await updateFreightNewUserVerifyStatus({
        user_id: String(userId),
        status: String(status)
      });
      showSuccess("Verification status updated");
      reloadTable();
    } catch (e) {
      showError(e.message || "Failed");
    }
  };

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[1400px]">
        <FreightAdminTableToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search (server-side)…"
          columnDefs={orderedColumnDefs}
          visibility={visibility}
          onToggleColumn={toggleColumn}
          onResetColumns={resetColumnsVisible}
          onResetColumnOrder={resetColumnOrder}
        />

        <div className="card offers-table">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
            <h3 className="m-0">
              New freight users ({recordsFiltered}
              {recordsTotal !== recordsFiltered ? ` of ${recordsTotal}` : ""})
            </h3>
            <DataTablePageSize value={pageSize} onChange={setPageSize} options={lengthMenu} />
          </div>

          <p className="text-sm text-gray-600 mb-3">
            Server-side paging, search, and sort. Use <strong>Columns</strong> in the toolbar for visibility; sort via
            column headers.
          </p>

          <FreightUsersServerList
            ref={tableRef}
            variant="new"
            columnDefs={orderedColumnDefs}
            visibility={visibility}
            search={debouncedSearch}
            onColumnReorder={onColumnReorder}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            lengthMenu={lengthMenu}
            getNavigate={getNavigate}
            onVerify={verify}
            onDeactivate={deactivate}
            onCountsChange={handleCounts}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightNewUsersPage;
